import { useState, useEffect, useRef } from 'react';
import { adminApi, getImageUrl } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Save, Plus, Trash2, Image, Type, Upload, X, ClipboardList, User, Calendar as CalendarIcon, Link, Unlink, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { SumUpPaymentsSection } from './settings/SumUpPaymentsSection';
import { GiftVoucherSettingsSection } from './settings/GiftVoucherSettingsSection';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  
  // Google Calendar state
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [calendarConfig, setCalendarConfig] = useState({
    client_id: '',
    client_secret: '',
    redirect_uri: '',
    calendar_id: 'primary'
  });
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  
  const logoInputRef = useRef(null);
  const aboutPhotoInputRef = useRef(null);
  const contactInputRef = useRef(null);
  const heroInputRefs = useRef({});

  useEffect(() => { loadSettings(); loadCalendarStatus(); }, []);

  // Check URL params for calendar connection result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      toast.success('Google Calendar connected successfully!');
      loadCalendarStatus();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (params.get('calendar_error')) {
      toast.error(`Calendar error: ${params.get('calendar_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loadCalendarStatus = async () => {
    try {
      const [statusRes, configRes] = await Promise.all([
        adminApi.getGoogleCalendarStatus(),
        adminApi.getGoogleCalendarConfig()
      ]);
      setCalendarStatus(statusRes.data);
      if (configRes.data.config) {
        setCalendarConfig({
          client_id: configRes.data.config.client_id || '',
          client_secret: configRes.data.config.client_secret_set ? '••••••••' : '',
          redirect_uri: configRes.data.config.redirect_uri || '',
          calendar_id: configRes.data.config.calendar_id || 'primary'
        });
      }
    } catch (error) {
      console.error('Failed to load calendar status:', error);
    }
  };

  const saveCalendarConfig = async () => {
    setSavingCalendar(true);
    try {
      await adminApi.saveGoogleCalendarConfig(calendarConfig);
      toast.success('Calendar configuration saved');
      loadCalendarStatus();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save');
    } finally {
      setSavingCalendar(false);
    }
  };

  const connectCalendar = async () => {
    setConnectingCalendar(true);
    try {
      const response = await adminApi.getGoogleCalendarAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start connection');
      setConnectingCalendar(false);
    }
  };

  const disconnectCalendar = async () => {
    if (!confirm('Are you sure you want to disconnect Google Calendar?')) return;
    try {
      await adminApi.disconnectGoogleCalendar();
      toast.success('Calendar disconnected');
      loadCalendarStatus();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const testCalendarConnection = async () => {
    try {
      const response = await adminApi.testGoogleCalendar();
      toast.success(`Connection OK! ${response.data.busy_slots_today} busy slots found today.`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Connection test failed');
    }
  };

  const loadSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      const data = response.data.settings;
      
      if (!data.images) {
        data.images = {
          logo_url: '/images/logo.png',
          hero_images: ['/images/hero-1.jpg', '/images/hero-2.jpg', '/images/hero-3.jpg'],
          contact_image_url: '/images/contact-dove.jpg'
        };
      }

      if (!data.hero_content) {
        data.hero_content = {
          title: 'Welcome to White Dove Wellness Holistic Therapies',
          subtitle: 'Experience the healing power of holistic therapies in a serene and nurturing environment.',
          button_text: 'Book Your Session'
        };
      }
      
      setSettings(data);
    } catch (error) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.updateSettings(settings);
      toast.success('Settings saved');
    } catch (error) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateSocialLink = (key, value) => {
    setSettings({
      ...settings,
      social_links: { ...settings.social_links, [key]: value }
    });
  };

  const updateImage = (key, value) => {
    setSettings({
      ...settings,
      images: { ...settings.images, [key]: value }
    });
  };

  const updateHeroContent = (key, value) => {
    setSettings({
      ...settings,
      hero_content: { ...settings.hero_content, [key]: value }
    });
  };

  const addBenefit = () => {
    const benefits = [...(settings?.hero_content?.benefits || []), ''];
    setSettings({
      ...settings,
      hero_content: { ...settings.hero_content, benefits }
    });
  };

  const updateBenefit = (index, value) => {
    const benefits = [...(settings?.hero_content?.benefits || [])];
    benefits[index] = value;
    setSettings({
      ...settings,
      hero_content: { ...settings.hero_content, benefits }
    });
  };

  const removeBenefit = (index) => {
    const benefits = [...(settings?.hero_content?.benefits || [])];
    benefits.splice(index, 1);
    setSettings({
      ...settings,
      hero_content: { ...settings.hero_content, benefits }
    });
  };

  const updateHeroImage = (index, value) => {
    const heroImages = [...(settings.images?.hero_images || [])];
    heroImages[index] = value;
    setSettings({
      ...settings,
      images: { ...settings.images, hero_images: heroImages }
    });
  };

  const addHeroImage = () => {
    const heroImages = [...(settings.images?.hero_images || []), ''];
    setSettings({
      ...settings,
      images: { ...settings.images, hero_images: heroImages }
    });
  };

  const removeHeroImage = (index) => {
    const heroImages = [...(settings.images?.hero_images || [])];
    heroImages.splice(index, 1);
    setSettings({
      ...settings,
      images: { ...settings.images, hero_images: heroImages }
    });
  };

  // Generic file upload handler
  const handleFileUpload = async (file, onSuccess, uploadKey) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, GIF, WebP, or SVG)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(prev => ({ ...prev, [uploadKey]: true }));
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await adminApi.uploadImage(formData);
      if (response.data.success) {
        onSuccess(response.data.url);
        toast.success('Image uploaded');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  // Logo upload
  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    handleFileUpload(file, (url) => updateImage('logo_url', url), 'logo');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  // Contact image upload
  const handleContactUpload = (e) => {
    const file = e.target.files?.[0];
    handleFileUpload(file, (url) => updateImage('contact_image_url', url), 'contact');
    if (contactInputRef.current) contactInputRef.current.value = '';
  };

  // Hero image upload
  const handleHeroUpload = (e, index) => {
    const file = e.target.files?.[0];
    handleFileUpload(file, (url) => updateHeroImage(index, url), `hero_${index}`);
    if (heroInputRefs.current[index]) heroInputRefs.current[index].value = '';
  };

  // About Me helpers
  const updateAboutMe = (key, value) => {
    setSettings({
      ...settings,
      about_me: { ...settings?.about_me, [key]: value }
    });
  };

  const addQualification = () => {
    const qualifications = [...(settings?.about_me?.qualifications || []), ''];
    setSettings({
      ...settings,
      about_me: { ...settings?.about_me, qualifications }
    });
  };

  const updateQualification = (index, value) => {
    const qualifications = [...(settings?.about_me?.qualifications || [])];
    qualifications[index] = value;
    setSettings({
      ...settings,
      about_me: { ...settings?.about_me, qualifications }
    });
  };

  const removeQualification = (index) => {
    const qualifications = [...(settings?.about_me?.qualifications || [])];
    qualifications.splice(index, 1);
    setSettings({
      ...settings,
      about_me: { ...settings?.about_me, qualifications }
    });
  };

  // About photo upload
  const handleAboutPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    handleFileUpload(file, (url) => updateAboutMe('photo_url', url), 'about_photo');
    if (aboutPhotoInputRef.current) aboutPhotoInputRef.current.value = '';
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="admin-settings">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-slate-800">Site Settings</h1>
        <p className="text-slate-600 mt-1">Manage your website settings, images, and social links</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        {/* Hero Content */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Type size={20} className="text-[#9F87C4]" />
            <h2 className="font-serif text-xl text-slate-800">Hero Section Content</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Title</label>
              <Input 
                value={settings?.hero_content?.title || ''} 
                onChange={(e) => updateHeroContent('title', e.target.value)} 
                className="mt-1" 
                placeholder="Welcome to White Dove Wellness"
                data-testid="hero-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Subtitle</label>
              <Textarea 
                value={settings?.hero_content?.subtitle || ''} 
                onChange={(e) => updateHeroContent('subtitle', e.target.value)} 
                className="mt-1" 
                placeholder="Experience the healing power of holistic therapies..."
                rows={3}
                data-testid="hero-subtitle-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Details (additional text below subtitle)</label>
              <Textarea 
                value={settings?.hero_content?.details || ''} 
                onChange={(e) => updateHeroContent('details', e.target.value)} 
                className="mt-1" 
                placeholder="Add more details about your services..."
                rows={4}
                data-testid="hero-details-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Benefits (bullet points)</label>
                <Button type="button" variant="outline" size="sm" onClick={addBenefit}>
                  <Plus size={14} className="mr-1" /> Add Benefit
                </Button>
              </div>
              <div className="space-y-2">
                {(settings?.hero_content?.benefits || []).map((benefit, index) => (
                  <div key={index} className="flex gap-2">
                    <Input 
                      value={benefit} 
                      onChange={(e) => updateBenefit(index, e.target.value)} 
                      placeholder="Enter a benefit..."
                      data-testid={`hero-benefit-${index}-input`}
                    />
                    <Button type="button" variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => removeBenefit(index)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Button Text</label>
              <Input 
                value={settings?.hero_content?.button_text || ''} 
                onChange={(e) => updateHeroContent('button_text', e.target.value)} 
                className="mt-1" 
                placeholder="Book Your Session"
                data-testid="hero-button-input"
              />
            </div>
          </div>
        </div>

        {/* Business Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-serif text-xl text-slate-800 mb-4">Business Information</h2>
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-slate-700">Business Name</label><Input value={settings?.business_name || ''} onChange={(e) => setSettings({ ...settings, business_name: e.target.value })} className="mt-1" /></div>
            <div><label className="text-sm font-medium text-slate-700">Tagline</label><Input value={settings?.tagline || ''} onChange={(e) => setSettings({ ...settings, tagline: e.target.value })} className="mt-1" /></div>
            <div><label className="text-sm font-medium text-slate-700">Email</label><Input type="email" value={settings?.email || ''} onChange={(e) => setSettings({ ...settings, email: e.target.value })} className="mt-1" /></div>
            <div><label className="text-sm font-medium text-slate-700">Phone</label><Input value={settings?.phone || ''} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} className="mt-1" /></div>
            <div><label className="text-sm font-medium text-slate-700">Address</label><Input value={settings?.address || ''} onChange={(e) => setSettings({ ...settings, address: e.target.value })} className="mt-1" /></div>
          </div>
        </div>

        {/* About Me */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <User size={20} className="text-[#9F87C4]" />
            <h2 className="font-serif text-xl text-slate-800">About Me Section</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">This section appears above the Contact section on the homepage when enabled</p>
          
          <div className="space-y-4">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <input
                type="checkbox"
                id="about_me_enabled"
                checked={settings?.about_me?.enabled || false}
                onChange={(e) => updateAboutMe('enabled', e.target.checked)}
                className="rounded border-slate-300 w-5 h-5"
              />
              <label htmlFor="about_me_enabled" className="text-sm font-medium text-slate-700 cursor-pointer">
                Show About Me section on homepage
              </label>
            </div>

            {/* Name */}
            <div>
              <label className="text-sm font-medium text-slate-700">Name / Title</label>
              <Input 
                value={settings?.about_me?.name || ''} 
                onChange={(e) => updateAboutMe('name', e.target.value)} 
                className="mt-1" 
                placeholder="e.g. Sarah Smith, Reflexologist"
              />
            </div>

            {/* Bio */}
            <div>
              <label className="text-sm font-medium text-slate-700">Bio</label>
              <Textarea 
                value={settings?.about_me?.bio || ''} 
                onChange={(e) => updateAboutMe('bio', e.target.value)} 
                className="mt-1" 
                rows={5}
                placeholder="Tell your story... your journey, passion for holistic therapies, etc."
              />
            </div>

            {/* Qualifications */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Qualifications</label>
                <Button type="button" variant="outline" size="sm" onClick={addQualification}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {(settings?.about_me?.qualifications || []).map((qual, index) => (
                  <div key={index} className="flex gap-2">
                    <Input 
                      value={qual} 
                      onChange={(e) => updateQualification(index, e.target.value)} 
                      placeholder="e.g. Level 3 Diploma in Reflexology"
                    />
                    <Button type="button" variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => removeQualification(index)}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
                {(settings?.about_me?.qualifications || []).length === 0 && (
                  <p className="text-sm text-slate-400 py-2">No qualifications added yet</p>
                )}
              </div>
            </div>

            {/* Photo */}
            <div>
              <label className="text-sm font-medium text-slate-700">Photo</label>
              <div className="mt-2 space-y-2">
                {settings?.about_me?.photo_url && (
                  <div className="relative inline-block">
                    <img src={getImageUrl(settings.about_me.photo_url)} alt="About me" className="h-32 w-auto object-cover rounded-lg border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => updateAboutMe('photo_url', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={aboutPhotoInputRef} type="file" accept="image/*" onChange={handleAboutPhotoUpload} className="hidden" id="about-photo-upload" />
                  <Button type="button" variant="outline" onClick={() => aboutPhotoInputRef.current?.click()} disabled={uploading.about_photo} className="flex items-center gap-2">
                    <Upload size={16} />
                    {uploading.about_photo ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Image size={20} className="text-[#9F87C4]" />
            <h2 className="font-serif text-xl text-slate-800">Site Images</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Upload images or enter URLs</p>
          
          <div className="space-y-6">
            {/* Logo */}
            <div>
              <label className="text-sm font-medium text-slate-700">Logo Image</label>
              <div className="mt-2 space-y-2">
                {settings?.images?.logo_url && (
                  <div className="relative inline-block">
                    <img src={getImageUrl(settings.images.logo_url)} alt="Logo preview" className="h-16 object-contain bg-slate-50 rounded border border-slate-200 p-2" />
                    <button
                      type="button"
                      onClick={() => updateImage('logo_url', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" />
                  <Button type="button" variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploading.logo} className="flex items-center gap-2">
                    <Upload size={16} />
                    {uploading.logo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <span className="text-xs text-slate-500">or enter URL below</span>
                </div>
                <Input 
                  value={settings?.images?.logo_url || ''} 
                  onChange={(e) => updateImage('logo_url', e.target.value)} 
                  placeholder="/images/logo.png or https://..."
                  data-testid="logo-url-input"
                />
              </div>
            </div>

            {/* Hero Images */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Hero Images (Desktop shows 3, Mobile shows 1)</label>
                <Button type="button" variant="outline" size="sm" onClick={addHeroImage}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-3">
                {(settings?.images?.hero_images || []).map((url, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-500">Hero Image {index + 1}</span>
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 ml-auto h-6 px-2" onClick={() => removeHeroImage(index)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    {url && (
                      <div className="relative inline-block">
                        <img src={getImageUrl(url)} alt={`Hero ${index + 1}`} className="h-20 w-32 object-cover rounded border border-slate-200" />
                        <button
                          type="button"
                          onClick={() => updateHeroImage(index, '')}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <input 
                        ref={el => heroInputRefs.current[index] = el} 
                        type="file" 
                        accept="image/*" 
                        onChange={(e) => handleHeroUpload(e, index)} 
                        className="hidden" 
                        id={`hero-upload-${index}`} 
                      />
                      <Button type="button" variant="outline" size="sm" onClick={() => heroInputRefs.current[index]?.click()} disabled={uploading[`hero_${index}`]} className="flex items-center gap-2">
                        <Upload size={14} />
                        {uploading[`hero_${index}`] ? 'Uploading...' : 'Upload'}
                      </Button>
                      <Input 
                        value={url} 
                        onChange={(e) => updateHeroImage(index, e.target.value)} 
                        placeholder={`/images/hero-${index + 1}.jpg`}
                        className="flex-1"
                        data-testid={`hero-image-${index}-input`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Image */}
            <div>
              <label className="text-sm font-medium text-slate-700">Contact Section Image</label>
              <div className="mt-2 space-y-2">
                {settings?.images?.contact_image_url && (
                  <div className="relative inline-block">
                    <img src={getImageUrl(settings.images.contact_image_url)} alt="Contact preview" className="h-20 object-contain bg-slate-50 rounded border border-slate-200 p-2" />
                    <button
                      type="button"
                      onClick={() => updateImage('contact_image_url', '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X size={12} />
                    </button>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input ref={contactInputRef} type="file" accept="image/*" onChange={handleContactUpload} className="hidden" id="contact-upload" />
                  <Button type="button" variant="outline" onClick={() => contactInputRef.current?.click()} disabled={uploading.contact} className="flex items-center gap-2">
                    <Upload size={16} />
                    {uploading.contact ? 'Uploading...' : 'Upload Image'}
                  </Button>
                  <span className="text-xs text-slate-500">or enter URL below</span>
                </div>
                <Input 
                  value={settings?.images?.contact_image_url || ''} 
                  onChange={(e) => updateImage('contact_image_url', e.target.value)} 
                  placeholder="/images/contact-dove.jpg or https://..."
                  data-testid="contact-image-url-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-serif text-xl text-slate-800 mb-4">Social Links</h2>
          <div className="space-y-4">
            <div><label className="text-sm font-medium text-slate-700">Facebook URL</label><Input value={settings?.social_links?.facebook_url || ''} onChange={(e) => updateSocialLink('facebook_url', e.target.value)} className="mt-1" placeholder="https://facebook.com/..." data-testid="facebook-url-input" /></div>
            <div><label className="text-sm font-medium text-slate-700">Instagram URL</label><Input value={settings?.social_links?.instagram_url || ''} onChange={(e) => updateSocialLink('instagram_url', e.target.value)} className="mt-1" placeholder="https://instagram.com/..." data-testid="instagram-url-input" /></div>
            <div><label className="text-sm font-medium text-slate-700">Twitter URL</label><Input value={settings?.social_links?.twitter_url || ''} onChange={(e) => updateSocialLink('twitter_url', e.target.value)} className="mt-1" placeholder="https://twitter.com/..." /></div>
            <div><label className="text-sm font-medium text-slate-700">LinkedIn URL</label><Input value={settings?.social_links?.linkedin_url || ''} onChange={(e) => updateSocialLink('linkedin_url', e.target.value)} className="mt-1" placeholder="https://linkedin.com/..." /></div>
          </div>
        </div>

        {/* Consultation Form Options */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList size={20} className="text-[#9F87C4]" />
            <h2 className="font-serif text-xl text-slate-800">Consultation Form Options</h2>
          </div>
          <p className="text-sm text-slate-500 mb-6">Configure the dropdown options for client consultation forms</p>

          {/* Contra-indications */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Contra-indications List</label>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                const current = settings?.consultation_options?.contra_indications || [];
                setSettings({
                  ...settings,
                  consultation_options: {
                    ...settings?.consultation_options,
                    contra_indications: [...current, '']
                  }
                });
              }}>
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {(settings?.consultation_options?.contra_indications || []).map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => {
                      const updated = [...(settings?.consultation_options?.contra_indications || [])];
                      updated[index] = e.target.value;
                      setSettings({
                        ...settings,
                        consultation_options: { ...settings?.consultation_options, contra_indications: updated }
                      });
                    }}
                    placeholder="e.g. Diabetes, Epilepsy..."
                    className="flex-1"
                  />
                  <Button type="button" variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => {
                    const updated = [...(settings?.consultation_options?.contra_indications || [])];
                    updated.splice(index, 1);
                    setSettings({
                      ...settings,
                      consultation_options: { ...settings?.consultation_options, contra_indications: updated }
                    });
                  }}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
              {(settings?.consultation_options?.contra_indications || []).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">No items. Click "Add" to create.</p>
              )}
            </div>
          </div>

          {/* Treatment Objectives */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Treatment Objectives List</label>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                const current = settings?.consultation_options?.treatment_objectives || [];
                setSettings({
                  ...settings,
                  consultation_options: {
                    ...settings?.consultation_options,
                    treatment_objectives: [...current, '']
                  }
                });
              }}>
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {(settings?.consultation_options?.treatment_objectives || []).map((item, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={item}
                    onChange={(e) => {
                      const updated = [...(settings?.consultation_options?.treatment_objectives || [])];
                      updated[index] = e.target.value;
                      setSettings({
                        ...settings,
                        consultation_options: { ...settings?.consultation_options, treatment_objectives: updated }
                      });
                    }}
                    placeholder="e.g. Relaxation, Stress relief..."
                    className="flex-1"
                  />
                  <Button type="button" variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => {
                    const updated = [...(settings?.consultation_options?.treatment_objectives || [])];
                    updated.splice(index, 1);
                    setSettings({
                      ...settings,
                      consultation_options: { ...settings?.consultation_options, treatment_objectives: updated }
                    });
                  }}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
              {(settings?.consultation_options?.treatment_objectives || []).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">No items. Click "Add" to create.</p>
              )}
            </div>
          </div>

          {/* Lifestyle Questions - More complex structure */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-700">Lifestyle Questions</label>
              <Button type="button" variant="outline" size="sm" onClick={() => {
                const current = settings?.consultation_options?.lifestyle_questions || [];
                setSettings({
                  ...settings,
                  consultation_options: {
                    ...settings?.consultation_options,
                    lifestyle_questions: [...current, { id: `lifestyle_${Date.now()}`, label: '', type: 'text', options: [] }]
                  }
                });
              }}>
                <Plus size={14} className="mr-1" /> Add Question
              </Button>
            </div>
            <div className="space-y-3 max-h-64 overflow-y-auto p-2 bg-slate-50 rounded-lg">
              {(settings?.consultation_options?.lifestyle_questions || []).map((question, index) => (
                <div key={index} className="p-3 bg-white rounded-lg border border-slate-200">
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={question.label}
                      onChange={(e) => {
                        const updated = [...(settings?.consultation_options?.lifestyle_questions || [])];
                        updated[index] = { ...updated[index], label: e.target.value };
                        setSettings({
                          ...settings,
                          consultation_options: { ...settings?.consultation_options, lifestyle_questions: updated }
                        });
                      }}
                      placeholder="Question label (e.g. Energy Levels)"
                      className="flex-1"
                    />
                    <select
                      value={question.type}
                      onChange={(e) => {
                        const updated = [...(settings?.consultation_options?.lifestyle_questions || [])];
                        updated[index] = { ...updated[index], type: e.target.value };
                        setSettings({
                          ...settings,
                          consultation_options: { ...settings?.consultation_options, lifestyle_questions: updated }
                        });
                      }}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="text">Text Input</option>
                      <option value="select">Dropdown</option>
                    </select>
                    <Button type="button" variant="ghost" size="icon" className="text-red-500 shrink-0" onClick={() => {
                      const updated = [...(settings?.consultation_options?.lifestyle_questions || [])];
                      updated.splice(index, 1);
                      setSettings({
                        ...settings,
                        consultation_options: { ...settings?.consultation_options, lifestyle_questions: updated }
                      });
                    }}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  {question.type === 'select' && (
                    <div className="mt-2">
                      <label className="text-xs text-slate-500">Options (comma-separated)</label>
                      <Input
                        value={(question.options || []).join(', ')}
                        onChange={(e) => {
                          const updated = [...(settings?.consultation_options?.lifestyle_questions || [])];
                          updated[index] = { 
                            ...updated[index], 
                            options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          };
                          setSettings({
                            ...settings,
                            consultation_options: { ...settings?.consultation_options, lifestyle_questions: updated }
                          });
                        }}
                        placeholder="e.g. High, Average, Low"
                        className="mt-1"
                      />
                    </div>
                  )}
                </div>
              ))}
              {(settings?.consultation_options?.lifestyle_questions || []).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-2">No questions. Click "Add Question" to create.</p>
              )}
            </div>
          </div>
        </div>

        {/* Booking Settings */}
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6" data-testid="booking-settings-section">
          <div className="flex items-center gap-3 border-b pb-4">
            <div className="p-2 bg-[#F5F3FA] rounded-lg">
              <CalendarIcon size={20} className="text-[#9F87C4]" />
            </div>
            <h2 className="font-serif text-xl text-slate-800">Online Booking Settings</h2>
          </div>

          {/* Enable Booking */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="booking_enabled"
              checked={settings?.booking_settings?.enabled || false}
              onChange={(e) => setSettings({
                ...settings,
                booking_settings: { ...settings?.booking_settings, enabled: e.target.checked }
              })}
              className="w-5 h-5 rounded border-slate-300 text-[#9F87C4] focus:ring-[#9F87C4]"
            />
            <label htmlFor="booking_enabled" className="text-sm font-medium text-slate-700 cursor-pointer">
              Enable online booking
            </label>
          </div>

          {/* Gap Between Appointments */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Gap Between Appointments (minutes)
              </label>
              <Input
                type="number"
                min="0"
                max="120"
                value={settings?.booking_settings?.gap_between_appointments || 30}
                onChange={(e) => setSettings({
                  ...settings,
                  booking_settings: { ...settings?.booking_settings, gap_between_appointments: parseInt(e.target.value) || 0 }
                })}
                data-testid="gap-input"
              />
              <p className="text-xs text-slate-500 mt-1">Travel time between appointments</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Advance Booking Window (days)
              </label>
              <Input
                type="number"
                min="1"
                max="365"
                value={settings?.booking_settings?.advance_booking_days || 30}
                onChange={(e) => setSettings({
                  ...settings,
                  booking_settings: { ...settings?.booking_settings, advance_booking_days: parseInt(e.target.value) || 30 }
                })}
                data-testid="advance-days-input"
              />
              <p className="text-xs text-slate-500 mt-1">How far ahead clients can book</p>
            </div>
          </div>

          {/* Fixed Location Address */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Clinic/Fixed Location Address
            </label>
            <Textarea
              value={settings?.booking_settings?.fixed_location_address || ''}
              onChange={(e) => setSettings({
                ...settings,
                booking_settings: { ...settings?.booking_settings, fixed_location_address: e.target.value }
              })}
              placeholder="Enter your clinic address (used for fixed location days)..."
              rows={2}
            />
            <p className="text-xs text-slate-500 mt-1">This address is used for days where you work from a fixed location</p>
          </div>

          {/* Email Notifications */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="email_notifications"
              checked={settings?.booking_settings?.email_notifications_enabled || false}
              onChange={(e) => setSettings({
                ...settings,
                booking_settings: { ...settings?.booking_settings, email_notifications_enabled: e.target.checked }
              })}
              className="w-5 h-5 rounded border-slate-300 text-[#9F87C4] focus:ring-[#9F87C4]"
              disabled={true}
            />
            <label htmlFor="email_notifications" className="text-sm font-medium text-slate-700 cursor-pointer">
              Send email confirmations (requires email setup)
            </label>
          </div>

          {/* Payment Settings */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Payment Settings</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="require_online_payment"
                  checked={settings?.booking_settings?.require_online_payment || false}
                  onChange={(e) => setSettings({
                    ...settings,
                    booking_settings: { ...settings?.booking_settings, require_online_payment: e.target.checked }
                  })}
                  className="w-5 h-5 rounded border-slate-300 text-[#9F87C4] focus:ring-[#9F87C4]"
                />
                <label htmlFor="require_online_payment" className="text-sm font-medium text-slate-700 cursor-pointer">
                  Require online payment (SumUp) to confirm booking
                </label>
              </div>
              
              {!settings?.booking_settings?.require_online_payment && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Complete Booking Button Text
                    </label>
                    <Input
                      value={settings?.booking_settings?.payment_button_text || 'Complete Booking Request'}
                      onChange={(e) => setSettings({
                        ...settings,
                        booking_settings: { ...settings?.booking_settings, payment_button_text: e.target.value }
                      })}
                      placeholder="Complete Booking Request"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Confirmation Message
                    </label>
                    <Textarea
                      value={settings?.booking_settings?.confirmation_message || ''}
                      onChange={(e) => setSettings({
                        ...settings,
                        booking_settings: { ...settings?.booking_settings, confirmation_message: e.target.value }
                      })}
                      placeholder="Your booking request has been submitted. We will confirm your appointment shortly."
                      rows={2}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Working Hours */}
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-3">Working Hours & Location</h3>
            <p className="text-xs text-slate-500 mb-4">Set your working hours and whether you're at a fixed location or working remotely on each day</p>
            <div className="space-y-3">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                const daySettings = settings?.booking_settings?.working_hours?.[day] || { enabled: false, start: '09:00', end: '17:00', location_type: 'both' };
                return (
                  <div key={day} className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-28">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={daySettings.enabled}
                          onChange={(e) => setSettings({
                            ...settings,
                            booking_settings: {
                              ...settings?.booking_settings,
                              working_hours: {
                                ...settings?.booking_settings?.working_hours,
                                [day]: { ...daySettings, enabled: e.target.checked }
                              }
                            }
                          })}
                          className="w-4 h-4 rounded border-slate-300 text-[#9F87C4] focus:ring-[#9F87C4]"
                        />
                        <span className="text-sm font-medium capitalize">{day}</span>
                      </label>
                    </div>
                    {daySettings.enabled && (
                      <>
                        <div className="flex items-center gap-2">
                          <Input
                            type="time"
                            value={daySettings.start}
                            onChange={(e) => setSettings({
                              ...settings,
                              booking_settings: {
                                ...settings?.booking_settings,
                                working_hours: {
                                  ...settings?.booking_settings?.working_hours,
                                  [day]: { ...daySettings, start: e.target.value }
                                }
                              }
                            })}
                            className="w-28"
                          />
                          <span className="text-slate-500">to</span>
                          <Input
                            type="time"
                            value={daySettings.end}
                            onChange={(e) => setSettings({
                              ...settings,
                              booking_settings: {
                                ...settings?.booking_settings,
                                working_hours: {
                                  ...settings?.booking_settings?.working_hours,
                                  [day]: { ...daySettings, end: e.target.value }
                                }
                              }
                            })}
                            className="w-28"
                          />
                        </div>
                        <select
                          value={daySettings.location_type || 'both'}
                          onChange={(e) => setSettings({
                            ...settings,
                            booking_settings: {
                              ...settings?.booking_settings,
                              working_hours: {
                                ...settings?.booking_settings?.working_hours,
                                [day]: { ...daySettings, location_type: e.target.value }
                              }
                            }
                          })}
                          className="px-3 py-2 text-sm border rounded-md bg-white focus:ring-2 focus:ring-[#9F87C4] focus:border-[#9F87C4]"
                        >
                          <option value="fixed">Fixed Location</option>
                          <option value="remote">Remote Only</option>
                          <option value="both">Both</option>
                        </select>
                      </>
                    )}
                    {!daySettings.enabled && (
                      <span className="text-sm text-slate-400">Closed</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Remote Day Message */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Remote Day Message</h3>
            <p className="text-xs text-slate-500 mb-2">Message shown to customers when they select a remote-only day</p>
            <Textarea
              value={settings?.booking_settings?.remote_day_message || ''}
              onChange={(e) => setSettings({
                ...settings,
                booking_settings: { ...settings?.booking_settings, remote_day_message: e.target.value }
              })}
              placeholder="Kelly is working remotely on this day. If you would like to book, please click next and enter your details to request an appointment."
              rows={3}
            />
          </div>

          {/* Calendar Colors */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-slate-700 mb-3">Calendar Day Colors</h3>
            <p className="text-xs text-slate-500 mb-3">Customize how different day types appear on the booking calendar</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Fixed Location Days</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings?.booking_settings?.calendar_colors?.fixed || '#9F87C4'}
                    onChange={(e) => setSettings({
                      ...settings,
                      booking_settings: {
                        ...settings?.booking_settings,
                        calendar_colors: {
                          ...settings?.booking_settings?.calendar_colors,
                          fixed: e.target.value
                        }
                      }
                    })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500">{settings?.booking_settings?.calendar_colors?.fixed || '#9F87C4'}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Remote Days</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings?.booking_settings?.calendar_colors?.remote || '#6BA8A0'}
                    onChange={(e) => setSettings({
                      ...settings,
                      booking_settings: {
                        ...settings?.booking_settings,
                        calendar_colors: {
                          ...settings?.booking_settings?.calendar_colors,
                          remote: e.target.value
                        }
                      }
                    })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500">{settings?.booking_settings?.calendar_colors?.remote || '#6BA8A0'}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Both Options Days</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings?.booking_settings?.calendar_colors?.both || '#8B9DC3'}
                    onChange={(e) => setSettings({
                      ...settings,
                      booking_settings: {
                        ...settings?.booking_settings,
                        calendar_colors: {
                          ...settings?.booking_settings?.calendar_colors,
                          both: e.target.value
                        }
                      }
                    })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500">{settings?.booking_settings?.calendar_colors?.both || '#8B9DC3'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gift Vouchers */}
        <GiftVoucherSettingsSection
          value={settings?.gift_voucher_settings}
          onChange={(gift_voucher_settings) => setSettings({ ...settings, gift_voucher_settings })}
        />

        {/* SumUp Payments */}
        <SumUpPaymentsSection />

        {/* Google Calendar Sync */}
        <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6" data-testid="google-calendar-section">
          <div className="flex items-center gap-3 border-b pb-4">
            <div className="p-2 bg-[#F5F3FA] rounded-lg">
              <Link size={20} className="text-[#9F87C4]" />
            </div>
            <div>
              <h2 className="font-serif text-xl text-slate-800">Google Calendar Sync</h2>
              <p className="text-sm text-slate-500">Connect your Google Calendar for two-way booking sync</p>
            </div>
          </div>

          {/* Connection Status */}
          <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-50">
            {calendarStatus?.connected ? (
              <>
                <CheckCircle className="text-green-500" size={24} />
                <div className="flex-1">
                  <p className="font-medium text-green-700">Connected</p>
                  <p className="text-sm text-slate-600">{calendarStatus.email}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={testCalendarConnection}
                  className="mr-2"
                >
                  <RefreshCw size={16} className="mr-1" />
                  Test
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={disconnectCalendar}
                >
                  <Unlink size={16} className="mr-1" />
                  Disconnect
                </Button>
              </>
            ) : calendarStatus?.configured ? (
              <>
                <AlertCircle className="text-amber-500" size={24} />
                <div className="flex-1">
                  <p className="font-medium text-amber-700">Configured but not connected</p>
                  <p className="text-sm text-slate-600">Click "Connect" to authorize access</p>
                </div>
                <Button
                  type="button"
                  onClick={connectCalendar}
                  disabled={connectingCalendar}
                  className="bg-[#9F87C4] hover:bg-[#8A6EB5]"
                >
                  <Link size={16} className="mr-1" />
                  {connectingCalendar ? 'Connecting...' : 'Connect Google Calendar'}
                </Button>
              </>
            ) : (
              <>
                <AlertCircle className="text-slate-400" size={24} />
                <div className="flex-1">
                  <p className="font-medium text-slate-600">Not configured</p>
                  <p className="text-sm text-slate-500">Add your Google OAuth credentials below</p>
                </div>
              </>
            )}
          </div>

          {/* OAuth Configuration */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-slate-700">OAuth Configuration</h3>
            <p className="text-xs text-slate-500">
              Get credentials from{' '}
              <a 
                href="https://console.cloud.google.com/apis/credentials" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#9F87C4] hover:underline"
              >
                Google Cloud Console
              </a>
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client ID
                </label>
                <Input
                  value={calendarConfig.client_id}
                  onChange={(e) => setCalendarConfig({ ...calendarConfig, client_id: e.target.value })}
                  placeholder="xxxxx.apps.googleusercontent.com"
                  data-testid="calendar-client-id"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client Secret
                </label>
                <Input
                  type="password"
                  value={calendarConfig.client_secret}
                  onChange={(e) => setCalendarConfig({ ...calendarConfig, client_secret: e.target.value })}
                  placeholder="Enter client secret"
                  data-testid="calendar-client-secret"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Redirect URI (optional)
                </label>
                <Input
                  value={calendarConfig.redirect_uri}
                  onChange={(e) => setCalendarConfig({ ...calendarConfig, redirect_uri: e.target.value })}
                  placeholder="Auto-detected if empty"
                />
                <p className="text-xs text-slate-500 mt-1">Leave empty to auto-detect</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Calendar ID
                </label>
                <Input
                  value={calendarConfig.calendar_id}
                  onChange={(e) => setCalendarConfig({ ...calendarConfig, calendar_id: e.target.value })}
                  placeholder="primary"
                />
                <p className="text-xs text-slate-500 mt-1">Use "primary" for main calendar</p>
              </div>
            </div>

            <Button
              type="button"
              onClick={saveCalendarConfig}
              disabled={savingCalendar || !calendarConfig.client_id}
              variant="outline"
            >
              <Save size={16} className="mr-2" />
              {savingCalendar ? 'Saving...' : 'Save Calendar Configuration'}
            </Button>
          </div>
        </div>

        <Button type="submit" disabled={saving} className="bg-[#9F87C4] hover:bg-[#8A6EB5]" data-testid="save-settings-btn">
          <Save size={18} className="mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>
    </div>
  );
}
